// src/components/ScrollLogo.tsx
import React, { useRef, useEffect } from 'react';
import {
  Box,
  Image,
  Flex,
  Link as ChakraLink,
  Container,
  chakra,
  shouldForwardProp,
  Button,
} from '@chakra-ui/react';
import {
  motion,
  useTransform,
  useScroll,
  isValidMotionProp,
  useSpring,
} from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

// 使用 chakra 工厂函数创建可动画化的组件
const MotionBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const ScrollLogo: React.FC = () => {
  const logoRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();

  // 自定义参数 - 调整为更精细的缩放范围
  const initialLogoY = 100; // Logo 初始位置
  const scaleStartY = 10; // 降低开始缩放的位置，让变化更早开始
  const scaleEndY = 200; // 减小缩放结束的位置，使缩放过程更集中
  const finalScale = 0.25; // 最终缩放比例
  const navbarHeight = 80; // 导航栏高度
  const logoWidth = 196; // Logo 宽度
  const logoHeight = 196; // Logo 高度

  // 使用更细的步进值进行转换，提高精度
  const yRaw = useTransform(
    scrollY,
    [0, scaleStartY],
    [initialLogoY, scaleStartY],
    { clamp: true }
  );

  // 使用 useSpring 使 y 值的变化更流畅，减少延迟感
  const y = useSpring(yRaw, {
    stiffness: 300, // 增加刚度，使响应更快
    damping: 20,    // 减少阻尼，使动画更敏感
    mass: 0.5,      // 减小质量，使动画更轻盈
  });

  // 创建更多的中间点来实现更精细的线性缩放
  const scaleRaw = useTransform(
    scrollY,
    [scaleStartY, scaleStartY + 50, scaleStartY + 100, scaleStartY + 150, scaleEndY],
    [1, 0.8, 0.6, 0.4, finalScale],
    { clamp: true }
  );

  // 应用 useSpring 使缩放更流畅
  const scale = useSpring(scaleRaw, {
    stiffness: 400, // 更高的刚度
    damping: 25,    // 适中的阻尼
    mass: 0.4,      // 更小的质量使动画更快响应
  });

  // 计算阴影显示条件：当内容滚动超过导航栏高度时显示阴影
  const boxShadow = useTransform(scrollY, (value) =>
    value > scaleEndY / 2 ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'
  );

  return (
    <MotionBox
      position="fixed"
      w="100%"
      zIndex="1000"
      style={{
        boxShadow,
        backdropFilter: 'blur(10px)', // 毛玻璃效果
        WebkitBackdropFilter: 'blur(10px)', // 兼容 Safari 等浏览器
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // 半透明背景
      }}
    >
      {/* 导航栏内容 */}
      <Container
        maxW="1200px"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        h={`${navbarHeight}px`}
      >
        {/* Logo部分 */}
        <Flex alignItems="center" height="80px" position="relative">
          <RouterLink to="/">
            <MotionBox
              ref={logoRef}
              position="absolute"
              top="0"
              style={{
                scale,
                y,
              }}
              transformOrigin="top left"
              zIndex="100"
              width={`${logoWidth}px`}
              height={`${logoHeight}px`}
              initial={false}
            >
              <Image
                src="/logo1024.png"
                alt="Logo"
                width="100%"
                height="100%"
              />
            </MotionBox>
          </RouterLink>
        </Flex>

        {/* 导航栏 - 重新设计 */}
        <Flex gap="30px" alignItems="center">
          <ChakraLink
            as={RouterLink}
            to="/solutions"
            fontSize="md"
            fontWeight="semibold"
            color="brand.black"
            _hover={{ color: "brand.purple", transform: "translateY(-2px)" }}
            transition="all 0.3s ease"
            position="relative"
            sx={{
              "&:after": {
                content: '""',
                position: "absolute",
                width: "0%",
                height: "2px",
                bottom: "-4px",
                left: "0",
                bg: "brand.purple",
                transition: "width 0.3s ease"
              },
              "&:hover:after": {
                width: "100%"
              }
            }}
          >
            解决方案
          </ChakraLink>
          {/* <ChakraLink
            as={RouterLink}
            to="/products"
            fontSize="md"
            fontWeight="semibold"
            color="brand.black"
            _hover={{ color: "brand.purple", transform: "translateY(-2px)" }}
            transition="all 0.3s ease"
            position="relative"
            sx={{
              "&:after": {
                content: '""',
                position: "absolute",
                width: "0%",
                height: "2px",
                bottom: "-4px",
                left: "0",
                bg: "brand.purple",
                transition: "width 0.3s ease"
              },
              "&:hover:after": {
                width: "100%"
              }
            }}
          >
            产品服务
          </ChakraLink> */}
          <ChakraLink
            as={RouterLink}
            to="/cases"
            fontSize="md"
            fontWeight="semibold"
            color="brand.black"
            _hover={{ color: "brand.purple", transform: "translateY(-2px)" }}
            transition="all 0.3s ease"
            position="relative"
            sx={{
              "&:after": {
                content: '""',
                position: "absolute",
                width: "0%",
                height: "2px",
                bottom: "-4px",
                left: "0",
                bg: "brand.purple",
                transition: "width 0.3s ease"
              },
              "&:hover:after": {
                width: "100%"
              }
            }}
          >
            成功案例
          </ChakraLink>
          {/* <ChakraLink
            as={RouterLink}
            to="/tech"
            fontSize="md"
            fontWeight="semibold"
            color="brand.black"
            _hover={{ color: "brand.purple", transform: "translateY(-2px)" }}
            transition="all 0.3s ease"
            position="relative"
            sx={{
              "&:after": {
                content: '""',
                position: "absolute",
                width: "0%",
                height: "2px",
                bottom: "-4px",
                left: "0",
                bg: "brand.purple",
                transition: "width 0.3s ease"
              },
              "&:hover:after": {
                width: "100%"
              }
            }}
          >
            技术博客
          </ChakraLink> */}
          <ChakraLink
            as={RouterLink}
            to="/about"
            fontSize="md"
            fontWeight="semibold"
            color="brand.black"
            _hover={{ color: "brand.purple", transform: "translateY(-2px)" }}
            transition="all 0.3s ease"
            position="relative"
            sx={{
              "&:after": {
                content: '""',
                position: "absolute",
                width: "0%",
                height: "2px",
                bottom: "-4px",
                left: "0",
                bg: "brand.purple",
                transition: "width 0.3s ease"
              },
              "&:hover:after": {
                width: "100%"
              }
            }}
          >
            关于我们
          </ChakraLink>
          <Button
            as={RouterLink}
            to="/contact"
            size="sm"
            px={6}
            py={5}
            colorScheme="purple"
            variant="solid"
            bgGradient="linear(to-r, brand.purple, purple.600)"
            _hover={{
              bgGradient: "linear(to-r, brand.purple, purple.700)",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)"
            }}
            transition="all 0.3s ease"
            borderRadius="full"
            fontWeight="bold"
          >
            联系我们
          </Button>
        </Flex>
      </Container>
    </MotionBox>
  );
};

export default ScrollLogo;